<template>
  <div class="fts-history" v-if="show">
    <div class="history-header">
      <div class="history-header-container">
        <div
          class="history-header-wrapper"
          @click="toggleMode('comment')"
          :class="{
            'history-header-wrapper--active': mode === 'comment',
          }"
          v-if="commentSection"
        >
          Comment
        </div>
        <div
          class="history-header-wrapper"
          @click="toggleMode('history')"
          :class="{
            'history-header-wrapper--active': mode === 'history',
          }"
        >
          History
        </div>
      </div>
    </div>

    <div class="history-section fts-scrollbar fts-scrollbar-overlay">
      <div class="history-section-spinner" v-if="loading">
        <fts-spinner show :size="30" />
      </div>

      <template v-else>
        <div v-for="history in histories" :key="history.id" class="history-wrapper">
          <fts-history-content
            :data="history"
            :locus-id="locusId"
            :vector-id="vectorId"
            :comment-section="commentSection"
            @comment="setShowComment(history)" 
          />
          <template v-if="commentSection && history.historyItems && history.historyItems.length">
            <fts-history-content
              v-for="item in history.historyItems" :key="item.id"
              :data="item"
              :vector-id="vectorId"
              :locus-id="locusId"
              child
              :comment-section="commentSection"
              @comment="setShowComment(history)"
            />
          </template>

          <fts-comment  
            :locus-id="locusId"
            :vector-id="vectorId"
            :locus-detail-id="!history.comment ? history.id : null"
            :locus-collaboration-id="history.comment !== false ? history.id : null"
            :avatar="false"
            :send-action="true"
            class="history-section-comment"
            v-model="history.collaboration"
            @added="addHistoryItems(history, $event)"
            v-if="history.showComment"
          />
        </div>

        <fts-collapsible-element
          :condition="initComment"
          immersive
          class="history-section-comment"
          v-if="commentSection"
        >
          <div slot="toggle-text" class="history-collapse-label">
            click to write a comment...
          </div>
          <fts-comment
            slot="content"
            :locus-id="locusId"
            :vector-id="vectorId"
            :avatar="false"
            :send-action="true"
            v-model="collaboration"
            @added="addHistory($event)"
          />
        </fts-collapsible-element>
      </template>
    </div>
  </div>
</template>

<script>
import { classificationStore } from "../../store/classificationStore";
import FTSComment from "../FTSComment";
import FTSHistoryContent from "./FTSHistoryContent";
import FTSCollapsibleElement from "../FTSCollapsibleElement";
import FTSSpinner from "../FTSSpinner";

export default {
  props: {
    locusId: {
      type: Number
    },
    vectorId: [String, Number],
    commentSection: {
      type: Boolean,
      default: true,
    },
    initOpen: Boolean,
    initComment: Boolean,
  },
  components: {
    "fts-history-content": FTSHistoryContent,
    "fts-comment": FTSComment,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-spinner": FTSSpinner,
  },
  data: () => ({
    show: false,
    loading: false,
    mode: "comment",
    histories: [],
    collaboration: {
      attachments: []
    },
  }),
  methods: {
    setShowComment(item) {
      if (!item.showComment)
        this.$set(item, 'showComment', true);
      else
        item.showComment = !item.showComment;      
    },
    addHistory(item) {
      this.histories.push(item);
      this.$emit("added");
    },
    addHistoryItems(history, item) {
      if (!history.historyItems) 
        this.$set(history, 'historyItems', []);

      history.historyItems.push(item);
      this.$emit("added");
    },
    async retrieveHistory() {
      if (this.locusId > 0 && this.show) {
        this.loading = true;
        try {
          let res = await classificationStore.dispatch("retrieveHistory", {
            id: this.locusId,
            history: this.mode == "history"
          });
          this.histories = res.data.histories;
        } catch(err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    async toggleHistory() {
      this.show = !this.show;
      await this.retrieveHistory();
      this.$emit("toggled", this.show);
    },
    async toggleMode(mode) {
      if (this.mode !== mode) {
        this.mode = mode;
        await this.retrieveHistory();
      }
    },
  },
  async mounted() {
    if (!this.commentSection) 
      this.mode = 'history';
    if (this.initOpen) 
      await this.toggleHistory();
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-history {
  $width: 400px !default;
  flex: 1 1 $width;
  max-width: $width;
  min-width: $width;
  display: flex;
  flex-direction: column;
  background: var(--fts-gray-1);
  .history-header {
    margin: 0.75rem 0.75rem 0.575rem;
    .history-header-container {
      display: flex;
      align-items: center;
      .history-header-wrapper {
        @include theme() {
          color: rgba(theme-get('fts-gray-5'), 0.65);
        }
        padding-bottom: 0.175rem;
        border-bottom: 2px solid transparent;
        font-weight: 500;
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        &:only-child {
          cursor: default;
        }
        &:not(:last-child) {
          margin-right: 1rem;
        }
        &:hover {
          @include theme() {
            color: rgba(theme-get('fts-gray-5'), 0.85);
          }
        }
        &.history-header-wrapper--active {
          @include theme() {
            border-color: rgba(theme-get('fts-gray-5'), 0.75);
          }
          color: var(--fts-gray-5);
        }
      }
    }
  }
  .history-section {
    flex: 1;
    padding-bottom: 1rem;
    .history-section-spinner {
      padding: 3rem;
    }
    .history-wrapper {
      > .history-section-comment {
        margin-left: calc(20px + 1.025rem);
        margin-right: .75rem;
        margin-bottom: .75rem;
      }
    }
    > .history-section-comment {
      margin-left: calc(0.75rem - 0.375rem);
      margin-right: .75rem;
      margin-top: 2rem;
    }
    .history-collapse-label {
      padding: .375rem;
    }
  }
}
</style>